import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Save, ArrowLeft, X, School as SchoolIcon, Sparkles, Loader2 } from "lucide-react";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { turkeyData, provinces } from "@/data/turkeyData";

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState({
    title: "", excerpt: "", content: "", category: "bursluluk",
    tags: [], cover_url: "", seo_title: "", seo_description: "",
    province: "", district: "", school_id: "", school_name: "", published: false,
  });
  const [loading, setLoading] = useState(!!id);
  const [schools, setSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState("");
  const [showSchoolPicker, setShowSchoolPicker] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: "", school_id: "", category: "bursluluk" });
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get(`${V1}/schools?limit=500`).then((r) => setSchools(r.data.items || []));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`${V1}/blog/id/${id}`)
      .then((r) => setPost({ ...r.data, tags: r.data.tags || [], school_id: r.data.school_id || "", school_name: r.data.school_name || "" }))
      .catch(() => toast.error("Yazı yüklenemedi"))
      .finally(() => setLoading(false));
  }, [id]);

  const save = async () => {
    try {
      const payload = {
        title: post.title, excerpt: post.excerpt, content: post.content,
        category: post.category, cover_url: post.cover_url,
        seo_title: post.seo_title, seo_description: post.seo_description,
        province: post.province || null, district: post.district || null,
        school_id: post.school_id || null,
        published: post.published, tags: post.tags,
      };
      if (id) {
        await api.patch(`${V1}/blog/${id}`, payload);
        toast.success("Güncellendi");
      } else {
        const { data } = await api.post(`${V1}/blog`, payload);
        toast.success("Oluşturuldu");
        navigate(`/admin/blog/duzenle/${data.id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const selectSchool = (s) => {
    setPost({ ...post, school_id: s.id, school_name: s.name, province: s.province || post.province, district: s.district || post.district });
    setShowSchoolPicker(false); setSchoolSearch("");
  };

  const clearSchool = () => setPost({ ...post, school_id: "", school_name: "" });

  const runAI = async () => {
    if (!aiForm.topic.trim()) { toast.error("Konu girin"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`${V1}/blog/ai/generate`, {
        topic: aiForm.topic,
        category: aiForm.category,
        school_id: aiForm.school_id || null,
      });
      toast.success("Taslak oluşturuldu");
      setAiOpen(false);
      navigate(`/admin/blog/duzenle/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI hatası"); }
    finally { setAiBusy(false); }
  };

  const filteredSchools = schools.filter(
    (s) => !schoolSearch || s.name.toLowerCase().includes(schoolSearch.toLowerCase()) || (s.province || "").toLowerCase().includes(schoolSearch.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-blog-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/blog")}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <div className="overline text-accent">Blog Düzenleyici</div>
            <h1 className="font-display text-2xl font-bold">{id ? "Yazıyı Düzenle" : "Yeni Yazı"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-blog-btn"><Sparkles className="h-4 w-4" /> AI ile Yaz</Button>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={post.published} onCheckedChange={(v) => setPost({ ...post, published: v })} data-testid="publish-toggle" />
            <span>{post.published ? "Yayında" : "Taslak"}</span>
          </label>
          <Button onClick={save} data-testid="save-blog-btn"><Save className="h-4 w-4" /> Kaydet</Button>
        </div>
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-md" data-testid="ai-blog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-accent" /> AI Blog Asistanı</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Konu / Ana Fikir</Label>
              <Textarea rows={3} value={aiForm.topic} onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })} placeholder="Örn: 8. sınıf öğrencileri için bursluluk sınavına hazırlık taktikleri" data-testid="ai-topic" />
            </div>
            <div>
              <Label>Okul (opsiyonel)</Label>
              <select className="w-full h-10 px-3 border border-border rounded" value={aiForm.school_id} onChange={(e) => setAiForm({ ...aiForm, school_id: e.target.value })} data-testid="ai-school-select">
                <option value="">— Genel (okul bağlı değil) —</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.province})</option>)}
              </select>
              <div className="text-[10px] text-muted-foreground mt-1">Okul seçerseniz makale o okula bağlanır ve şehir/ilçe bağlamı ile üretilir.</div>
            </div>
            <div>
              <Label>Kategori</Label>
              <Input value={aiForm.category} onChange={(e) => setAiForm({ ...aiForm, category: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Vazgeç</Button>
            <Button onClick={runAI} disabled={aiBusy || !aiForm.topic.trim()} data-testid="ai-generate-btn">
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              1500-2000 Kelime Üret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {loading ? (
        <div className="p-10 text-center text-muted-foreground animate-pulse">Yazı yükleniyor…</div>
      ) : (
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div><Label>Başlık</Label><Input value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} data-testid="post-title" /></div>
          <div><Label>Özet</Label><Textarea rows={2} value={post.excerpt} onChange={(e) => setPost({ ...post, excerpt: e.target.value })} data-testid="post-excerpt" /></div>
          <div><Label>İçerik (Markdown)</Label><Textarea rows={20} value={post.content} onChange={(e) => setPost({ ...post, content: e.target.value })} className="font-mono text-sm" data-testid="post-content" /></div>
        </div>
        <div className="space-y-4">
          <div className="p-4 rounded-lg border border-border bg-card space-y-2">
            <Label className="flex items-center gap-2"><SchoolIcon className="h-4 w-4 text-primary" /> Okul Bağlantısı</Label>
            {post.school_id ? (
              <div className="flex items-center justify-between p-2 rounded bg-accent/5 border border-accent/40">
                <div className="text-sm font-medium truncate">{post.school_name}</div>
                <Button size="sm" variant="ghost" onClick={clearSchool} data-testid="clear-school-btn"><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setShowSchoolPicker(true)} className="w-full" data-testid="pick-school-btn">
                  Okul Seç
                </Button>
                {showSchoolPicker && (
                  <div className="border border-border rounded p-2 space-y-1 bg-background">
                    <Input placeholder="Okul veya il ara..." value={schoolSearch} onChange={(e) => setSchoolSearch(e.target.value)} className="h-8" autoFocus data-testid="school-search-input" />
                    <div className="max-h-64 overflow-y-auto divide-y divide-border">
                      {filteredSchools.slice(0, 30).map((s) => (
                        <button key={s.id} type="button" onClick={() => selectSchool(s)} className="w-full text-left p-2 hover:bg-muted/50 text-sm" data-testid={`school-option-${s.id}`}>
                          <div className="font-medium">{s.name}</div>
                          <div className="text-xs text-muted-foreground">{s.province} / {s.district}</div>
                        </button>
                      ))}
                      {filteredSchools.length === 0 && <div className="p-3 text-xs text-muted-foreground text-center">Sonuç yok</div>}
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="text-[10px] text-muted-foreground">Yazıyı belirli bir okula bağlarsanız o okulun detay sayfasında da listelenir.</div>
          </div>
          <div><Label>Kategori</Label><Input value={post.category} onChange={(e) => setPost({ ...post, category: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>İl</Label>
              <Select value={post.province || ""} onValueChange={(v) => setPost({ ...post, province: v, district: "" })}>
                <SelectTrigger><SelectValue placeholder="İl Seçiniz (İsteğe Bağlı)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Yok</SelectItem>
                  {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>İlçe</Label>
              <Select value={post.district || ""} onValueChange={(v) => setPost({ ...post, district: v })} disabled={!post.province}>
                <SelectTrigger><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Yok</SelectItem>
                  {post.province && turkeyData[post.province] ? turkeyData[post.province].map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  )) : null}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Kapak Görsel URL</Label><Input value={post.cover_url || ""} onChange={(e) => setPost({ ...post, cover_url: e.target.value })} /></div>
          <div><Label>SEO Başlık (60 karakter)</Label><Input value={post.seo_title || ""} onChange={(e) => setPost({ ...post, seo_title: e.target.value })} maxLength={70} /></div>
          <div><Label>SEO Açıklama (155 karakter)</Label><Textarea rows={3} value={post.seo_description || ""} onChange={(e) => setPost({ ...post, seo_description: e.target.value })} maxLength={200} /></div>
          <div><Label>Etiketler (virgülle)</Label><Input value={(post.tags || []).join(", ")} onChange={(e) => setPost({ ...post, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
        </div>
      </div>
      )}
    </div>
  );
}
